import { useRef, useState, useEffect } from 'react';
import { Link } from '@lmig/lmds-react-link';
import { SearchInput } from '@lmig/lmds-react-alphanumeric-input';
import {
  IconSuccessAlert,
  IconCloseCircle,
  IconAuto,
  IconUmbrella,
  IconProperty,
  IconWorkersComp,
  IconGeneralLiability,
  IconBroken,
} from '@lmig/lmds-react-icons';
import { SitecoreFetch } from 'Api/api/SitecoreApi';
import { HttpStatusCodes, IServerResponse } from 'Utilities/ClientResponseUtility';
import { getAppetiteLink } from 'Foundation/Configurations/GlobalConfiguration';
import { getEcliqLink } from 'Foundation/Configurations/GlobalConfiguration';
import {
  IAskKodiakProps,
  AskKodiakResponse,
  AskKodiakHit,
  AskKodiakProductAppetiteResponse,
  AskKodiakCompany,
  AskKodiakProductGrouping,
  AskKodiakProduct,
  AskKodiakProductAppetite,
  ProductCode,
  LIBERTY_MUTUAL_COMPANY_NAME,
  askKodiakAppetiteCodes,
  createProductAppetiteList,
} from './AskKodiakModels';
import './AskKodiak.scss';

export default function AskKodiak(props: IAskKodiakProps) {
  const appetiteLink = getAppetiteLink();
  const ecliqLink = getEcliqLink();
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLDivElement>(null);
  const searchTermRef = useRef<string>('');

  const [componentData, setComponentData] = useState<AskKodiakResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState<number>(-1);
  const [showNoResultsView, setShowNoResultsView] = useState<boolean>(false);

  const [selectedAK, setSelectedAK] = useState<AskKodiakHit | undefined>(undefined);
  const [productAppetiteData, setProductAppetiteData] = useState<AskKodiakProductAppetite[] | undefined>(undefined);
  const [responseAPIError, setResponseAPIError] = useState<boolean>(false);

  // Effect to handle click outside the search input
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node) &&
        searchTerm &&
        searchTerm.length > 0 &&
        componentData &&
        componentData.NbHits === 0 &&
        showDropdown
      ) {
        setShowNoResultsView(true);
        setShowDropdown(false);
        setSelectedResultIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchTerm, componentData, showDropdown]);

  const sanitizeInput = (query: string): string | null => {
    if (!query || query.length > 100) return null;

    const sanitized = query
      .trim()
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .toLowerCase();
    return sanitized.length > 0 ? sanitized : null;
  };

  const fetchAskKodiakSuggestion = async (query: string) => {
    const sanitizedQuery = sanitizeInput(query);
    if (!sanitizedQuery) return;

    try {
      const response = (await SitecoreFetch(
        `/api/sitecore/AskKodiakAsync/GetAskKodiakSuggestion?query=${sanitizedQuery}`,
      )) as IServerResponse;

      if (response?.Status?.Code === HttpStatusCodes.OK) {
        setComponentData(response.Data as AskKodiakResponse);
        setResponseAPIError(false);
        setShowDropdown(true);
        setSelectedResultIndex(-1);
        setShowNoResultsView(false);
      } else {
        setComponentData(null);
        setResponseAPIError(true);
        setShowDropdown(false);
        setSelectedResultIndex(-1);
      }
    } catch (error) {
      console.error('Error fetching Ask Kodiak suggestions:', error);
      setComponentData(null);
      setResponseAPIError(true);
      setShowDropdown(false);
      setSelectedResultIndex(-1);
    }
  };

  const fetchAskKodiakProductAppetite = async (hashCode: string) => {
    const sanitizedHashCode = sanitizeInput(hashCode);
    if (!sanitizedHashCode) return;

    try {
      const response = (await SitecoreFetch(
        `/api/sitecore/AskKodiakAsync/GetAskKodiakProductAppetite?hashCode=${sanitizedHashCode}`,
      )) as IServerResponse;

      if (response?.Status?.Code === HttpStatusCodes.OK) {
        const result = processProductAppetiteResponse(response.Data as AskKodiakProductAppetiteResponse);
        if (result.success) {
          setProductAppetiteData(result.data);
          setResponseAPIError(false);
        } else {
          setProductAppetiteData(undefined);
          setResponseAPIError(true);
        }
      } else {
        setProductAppetiteData(undefined);
        setResponseAPIError(true);
      }
    } catch (error) {
      console.error('Error fetching Ask Kodiak product appetite:', error);
      setProductAppetiteData(undefined);
      setResponseAPIError(true);
    }
  };

  const isProductEligibleForCode = (product: AskKodiakProduct, code: ProductCode): boolean => {
    return product.Eligible && product.CoverageType.includes(code);
  };

  const hasEligibleProductsForCode = (group: AskKodiakProductGrouping, code: ProductCode): boolean => {
    return (
      group.Products.length > 0 &&
      group.ProductCodes.length > 0 &&
      group.ProductCodes.includes(code) &&
      group.Products.some((product) => isProductEligibleForCode(product, code))
    );
  };

  const findEligibleAppetiteCodes = (company: AskKodiakCompany): Set<ProductCode> => {
    const eligibleCodes = new Set<ProductCode>();

    if (company.Name !== LIBERTY_MUTUAL_COMPANY_NAME || company.ProductGroupings.length === 0) {
      return eligibleCodes;
    }

    for (const code of askKodiakAppetiteCodes) {
      for (const group of company.ProductGroupings) {
        if (hasEligibleProductsForCode(group, code)) {
          eligibleCodes.add(code);
          break; // Found eligible products for this code, move to next code
        }
      }
    }

    return eligibleCodes;
  };

  const processProductAppetiteResponse = (
    data: AskKodiakProductAppetiteResponse | null,
  ): { success: boolean; data?: AskKodiakProductAppetite[] } => {
    if (!data) {
      return { success: false };
    }

    // Create fresh result array to avoid mutation
    const result = createProductAppetiteList();

    // Find all eligible codes across all companies
    const allEligibleCodes = new Set<ProductCode>();

    for (const company of data.Companies) {
      const companyEligibleCodes = findEligibleAppetiteCodes(company);
      companyEligibleCodes.forEach((code) => allEligibleCodes.add(code));
    }

    // Update result with eligible codes
    return {
      success: true,
      data: result.map((item) => ({
        ...item,
        isThereAppetite: allEligibleCodes.has(item.id),
      })),
    };
  };

  const getAskKodiakSuggestion = async (searchTermInput: string | null = null) => {
    setIsLoading(true);
    setSelectedAK(undefined);
    setProductAppetiteData(undefined);

    const searchTermLocal = searchTermInput?.toLowerCase() ?? searchTermRef?.current?.toLowerCase();

    if (searchTermLocal && searchTermLocal?.length > 0) {
      await fetchAskKodiakSuggestion(searchTermLocal);
    }

    setIsLoading(false);
  };

  const submitGetAskKodiakSuggestion = () => {
    if (searchTerm !== searchTermRef.current && searchTermRef?.current && searchTermRef.current.length > 0) {
      getAskKodiakSuggestion(searchTermRef.current);
    }
  };

  const handleSelectItem = async (hit: AskKodiakHit) => {
    setComponentData(null);
    setProductAppetiteData(undefined);
    setSelectedAK(hit);
    setSearchTerm(hit.Description);
    setShowDropdown(false);
    setSelectedResultIndex(-1);
    setShowNoResultsView(false);

    if (hit.Hash) {
      setIsLoading(true);
      await fetchAskKodiakProductAppetite(hit.Hash);
      setIsLoading(false);
    }
  };

  const handleClearInput = () => {
    setResponseAPIError(false);
    setSearchTerm('');
    searchTermRef.current = '';
    setComponentData(null);
    setSelectedAK(undefined);
    setProductAppetiteData(undefined);
    setShowDropdown(false);
    setSelectedResultIndex(-1);
    setShowNoResultsView(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const isAlphanumeric = /^[a-zA-Z0-9\s]*$/.test(value);

    setResponseAPIError(false);
    setShowNoResultsView(false); // Reset no results view when user types
    if (isAlphanumeric) {
      setSearchTerm(value);
      searchTermRef.current = value;
      submitGetAskKodiakSuggestion();
    } else {
      setSearchTerm(searchTermRef.current);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitGetAskKodiakSuggestion();
  };

  const handleInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitGetAskKodiakSuggestion();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['Backspace', 'Delete', 'Escape', 'Enter', 'ArrowUp', 'ArrowDown'];

    if (allowedKeys.includes(e.key)) {
      // Handle navigation keys when dropdown is visible
      if (showDropdown && componentData && componentData.Hits.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedResultIndex((prev) => (prev < componentData.Hits.length - 1 ? prev + 1 : prev));
          return;
        }

        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedResultIndex((prev) => (prev > -1 ? prev - 1 : -1));
          return;
        }

        if (e.key === 'Enter') {
          e.preventDefault();
          if (selectedResultIndex >= 0 && selectedResultIndex < componentData.Hits.length) {
            // Select the highlighted result
            handleSelectItem(componentData.Hits[selectedResultIndex]);
          } else {
            // Show no results view if no item is selected and no results
            if (componentData.NbHits === 0) {
              setShowNoResultsView(true);
              setShowDropdown(false);
            }
          }
          return;
        }

        if (e.key === 'Escape') {
          e.preventDefault();
          if (componentData.NbHits === 0) {
            setShowNoResultsView(true);
          }
          setShowDropdown(false);
          setSelectedResultIndex(-1);
          return;
        }
      } else {
        // Handle Enter/Escape when dropdown is not visible
        if (e.key === 'Enter' || e.key === 'Escape') {
          if (searchTerm && searchTerm.length > 0 && (!componentData || componentData.NbHits === 0)) {
            setShowNoResultsView(true);
            setShowDropdown(false);
          }
          return;
        }
      }

      // Allow other navigation keys
      return;
    }

    if (!/^[a-zA-Z0-9\s]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const highlightText = (text: string) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="ak-search-highlight">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const renderProductIcon = (productId: ProductCode) => {
    const iconProps = { size: '16' as const };

    switch (productId) {
      case 'AUTOB':
        return <IconAuto {...iconProps} />;
      case 'PROPC':
        return <IconProperty {...iconProps} />;
      case 'GL':
        return <IconGeneralLiability {...iconProps} />;
      case 'WORK':
        return <IconWorkersComp {...iconProps} />;
      case 'UMBRC':
        return <IconUmbrella {...iconProps} />;
      default:
        return null;
    }
  };

  const renderAppetiteItem = (item: AskKodiakProductAppetite) => (
    <div key={item.id} className="ak-appetite-item">
      <div className="ak-appetite-item-iconName">
        {renderProductIcon(item.id)}
        <span className="ak-appetite-item-title">{item.name}</span>
      </div>
      {item.isThereAppetite ? (
        <IconSuccessAlert color="positive" size="16" />
      ) : (
        <IconCloseCircle color="negative" size="16" />
      )}
    </div>
  );

  const renderAppetiteResults = () => {
    if (!productAppetiteData || productAppetiteData.length === 0) {
      return null;
    }

    return (
      <div className="ak-countrywide-appetite">
        <div className="ak-appetite-upper-content">
          <div className="ak-appetite-header">
            <IconSuccessAlert color="positive" size="16" />
            <h3 className="ak-appetite-title">Countrywide appetite</h3>
          </div>
          <div className="ak-appetite-items">{productAppetiteData.map(renderAppetiteItem)}</div>
          {ecliqLink && ecliqLink.length > 0 && (
            <Link className="ak-appetite-ecliq" href={ecliqLink} variant="standalone" target="_blank">
              Quote in eCLIQ<span>&reg;</span>
            </Link>
          )}
        </div>
        <div className="ak-appetite-bottom-content">
          <div className="ak-appetite-footer">
            <p className="ak-powered-by">
              Countrywide results: Consult eCLIQ<span>&reg;</span> for more detail. Powered by Ask Kodiak
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderSearchResultItem = (hit: AskKodiakHit, index: number) => (
    <div
      className={`ak-search-result-text${index === selectedResultIndex ? ' ak-search-result-selected' : ''}`}
      key={hit.ObjectID}
      onClick={() => handleSelectItem(hit)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelectItem(hit);
        }
      }}
    >
      {highlightText(hit.Description)}
    </div>
  );

  const renderSearchResults = () => {
    if (!componentData || !showDropdown) return null;

    if (componentData.NbHits > 0) {
      return (
        <div className="ak-search-results">
          <div className="ak-search-results-items">
            {componentData.Hits.map((hit, index) => renderSearchResultItem(hit, index))}
          </div>
        </div>
      );
    } else {
      return (
        <div className="ak-no-results">
          <div className={`ak-search-result-text`}>{`No results found for '${searchTerm}'`}</div>
        </div>
      );
    }
  };

  const renderOutOfAppetiteView = () => {
    return (
      <>
        <div className="ak-out-of-appetite-view">
          <IconCloseCircle color="negative" size="32" />
          <p className="ak-out-of-appetite-message">Out of appetite</p>
        </div>
        <div className="ak-powered-by">Powered by Ask Kodiak</div>
      </>
    );
  };

  const renderDefaultContent = () => (
    <>
      {props?.richtext && <div className="ak-rich-text" dangerouslySetInnerHTML={{ __html: props.richtext }}></div>}
      <div className="ak-powered-by">Powered by Ask Kodiak</div>
    </>
  );

  const renderResponseAPIError = () => {
    if (responseAPIError) {
      return (
        <div className="ak-error">
          <IconBroken size="32" />
          <span className="ak-error-message">Page currently unavailable</span>
          {ecliqLink && ecliqLink.length > 0 && (
            <Link className="ak-appetite-ecliq" href={ecliqLink} variant="standalone" target="_blank">
              Quote in eCLIQ<span>&reg;</span>
            </Link>
          )}
        </div>
      );
    }
  };

  const renderMainContent = () => {
    // Handle selected item state (showing appetite results)
    if (selectedAK !== undefined) {
      if (isLoading) {
        return <div className="ak-loading">Loading appetite data...</div>;
      }

      // Render Out of Appetite view if no item in the productAppetiteData has a true value of hasAppetite
      if (!productAppetiteData?.find((item) => item.isThereAppetite)) {
        return renderOutOfAppetiteView();
      }

      return renderAppetiteResults();
    }

    // Handle no results view (appears between silly-underline-box and powered-by)
    if (showNoResultsView && searchTerm && searchTerm.length > 0) {
      return renderOutOfAppetiteView();
    }

    // Handle empty search state (show default content)
    if (!searchTerm || searchTerm.length === 0) {
      return renderDefaultContent();
    }

    // Handle search with results state (shows dropdown)
    if (searchTerm && searchTerm.length > 0) {
      if (isLoading) {
        return <div className="ak-loading">Loading...</div>;
      }
      return renderSearchResults();
    }

    return null;
  };

  return (
    <>
      <div className="ask-kodiak" ref={containerRef}>
        <div className="ak-heading-plus-icon">
          <h4 className="ak-appetite">Appetite</h4>
          {appetiteLink && appetiteLink.length > 0 && (
            <Link className="ak-link" href={appetiteLink} variant="standalone" target="_blank">
              View appetite
            </Link>
          )}
        </div>

        {!responseAPIError ? (
          <div className={`ak-body${!searchTerm || searchTerm.length === 0 ? '' : ' ak-search-results-loaded'}`}>
            <div className="ak-search-input-evo" ref={searchInputRef}>
              <form className="ak-search-form" onSubmit={handleFormSubmit}>
                <div className="ak-search-form-group">
                  <div className="ak-label-and-input">
                    <SearchInput
                      className="ak-input"
                      labelVisual="Enter description / NAICS code"
                      value={searchTerm}
                      onKeyDown={handleKeyDown}
                      onChange={handleInputChange}
                      onSubmit={handleInputSubmit}
                      id="ask-kodiak-search-input"
                      aria-live="polite"
                      onClearClicked={handleClearInput}
                    />
                  </div>
                </div>
              </form>
            </div>
            <div className="ak-silly-underline-box"></div>
            {renderMainContent()}
          </div>
        ) : (
          renderResponseAPIError()
        )}
      </div>
    </>
  );
}
